import { create } from "zustand";
import { persist } from "zustand/middleware";
import { analytics } from "../utils/analytics";

export interface UserPreferences {
  // Accessibility
  accessibility: {
    highContrast: boolean;
    reducedMotion: boolean;
    fontSize: "small" | "medium" | "large";
    screenReader: boolean;
    keyboardNavigation: boolean;
  };

  // Audio/Voice
  audio: {
    voiceEnabled: boolean;
    ttsEnabled: boolean;
    autoSpeak: boolean;
    voiceSpeed: number; // 0.5 - 2.0
    voiceVolume: number; // 0 - 1
    voicePitch: number; // 0.5 - 2.0
    preferredVoice?: string;
  };

  // Interface
  interface: {
    theme: "light" | "dark" | "auto";
    language: string;
    compactMode: boolean;
    animationsEnabled: boolean;
    notificationsEnabled: boolean;
    tooltipsEnabled: boolean;
  };

  // Privacy
  privacy: {
    analyticsEnabled: boolean;
    performanceMonitoring: boolean;
    crashReporting: boolean;
    locationTracking: boolean;
    cookiesEnabled: boolean;
  };

  // Features
  features: {
    aiChatEnabled: boolean;
    donationsEnabled: boolean;
    premiumFeatures: boolean;
    experimentalFeatures: boolean;
  };

  // User Context
  context: {
    savedZipCode?: string;
    savedNeeds: string[];
    preferredContactMethod: "phone" | "email" | "text";
    emergencyContact?: string;
    language: string;
  };

  // Onboarding
  onboarding: {
    completed: boolean;
    currentStep: number;
    skippedSteps: string[];
    completedTutorials: string[];
  };
}

interface UserPreferencesStore extends UserPreferences {
  // Actions
  updateAccessibility: (
    accessibility: Partial<UserPreferences["accessibility"]>
  ) => void;
  updateAudio: (audio: Partial<UserPreferences["audio"]>) => void;
  updateInterface: (
    interfacePrefs: Partial<UserPreferences["interface"]>
  ) => void;
  updatePrivacy: (privacy: Partial<UserPreferences["privacy"]>) => void;
  updateFeatures: (features: Partial<UserPreferences["features"]>) => void;
  updateContext: (context: Partial<UserPreferences["context"]>) => void;
  updateOnboarding: (
    onboarding: Partial<UserPreferences["onboarding"]>
  ) => void;

  // Utility actions
  resetToDefaults: () => void;
  exportPreferences: () => string;
  importPreferences: (preferences: string) => boolean;
  getPreferencesSummary: () => Record<string, unknown>;
}

const defaultPreferences: UserPreferences = {
  accessibility: {
    highContrast: false,
    reducedMotion: false,
    fontSize: "medium",
    screenReader: false,
    keyboardNavigation: true,
  },
  audio: {
    voiceEnabled: true,
    ttsEnabled: true,
    autoSpeak: false,
    voiceSpeed: 1.0,
    voiceVolume: 0.8,
    voicePitch: 1.0,
  },
  interface: {
    theme: "auto",
    language: "EN",
    compactMode: false,
    animationsEnabled: true,
    notificationsEnabled: true,
    tooltipsEnabled: true,
  },
  privacy: {
    analyticsEnabled: true,
    performanceMonitoring: true,
    crashReporting: true,
    locationTracking: false,
    cookiesEnabled: true,
  },
  features: {
    aiChatEnabled: true,
    donationsEnabled: true,
    premiumFeatures: false,
    experimentalFeatures: false,
  },
  context: {
    savedNeeds: [],
    preferredContactMethod: "phone",
    emergencyContact: "",
    language: "EN",
  },
  onboarding: {
    completed: false,
    currentStep: 0,
    skippedSteps: [],
    completedTutorials: [],
  },
};

export const useUserPreferencesStore = create<UserPreferencesStore>()(
  persist(
    (set, get) => ({
      ...defaultPreferences,

      updateAccessibility: (accessibility) => {
        set((state) => ({
          accessibility: { ...state.accessibility, ...accessibility },
        }));

        analytics.track("preferences_updated", "settings", {
          category: "accessibility",
          changes: accessibility,
        });
      },

      updateAudio: (audio) => {
        set((state) => ({
          audio: { ...state.audio, ...audio },
        }));

        analytics.track("preferences_updated", "settings", {
          category: "audio",
          changes: audio,
        });
      },
      updateInterface: (interfacePrefs) => {
        set((state) => ({
          interface: { ...state.interface, ...interfacePrefs },
        }));

        analytics.track("preferences_updated", "settings", {
          category: "interface",
          changes: interfacePrefs,
        });
      },

      updatePrivacy: (privacy) => {
        set((state) => ({
          privacy: { ...state.privacy, ...privacy },
        }));

        // Update analytics based on privacy settings
        if (privacy.analyticsEnabled !== undefined) {
          analytics.setEnabled(privacy.analyticsEnabled);
        }

        analytics.track("preferences_updated", "settings", {
          category: "privacy",
          changes: privacy,
        });
      },

      updateFeatures: (features) => {
        set((state) => ({
          features: { ...state.features, ...features },
        }));

        analytics.track("preferences_updated", "settings", {
          category: "features",
          changes: features,
        });
      },

      updateContext: (context) => {
        set((state) => ({
          context: { ...state.context, ...context },
        }));

        // Update analytics context
        if (context.savedZipCode || context.savedNeeds) {
          analytics.setLocationContext(
            context.savedZipCode,
            context.savedNeeds?.[0]
          );
        }

        analytics.track("preferences_updated", "settings", {
          category: "context",
          changes: context,
        });
      },

      updateOnboarding: (onboarding) => {
        set((state) => ({
          onboarding: { ...state.onboarding, ...onboarding },
        }));

        analytics.track("onboarding_updated", "onboarding", onboarding);
      },

      resetToDefaults: () => {
        set(defaultPreferences);
        analytics.track("preferences_reset", "settings");
      },

      exportPreferences: () => {
        const state = get();
        const exportData = {
          accessibility: state.accessibility,
          audio: state.audio,
          interface: state.interface,
          privacy: state.privacy,
          features: state.features,
          context: state.context,
          onboarding: state.onboarding,
          exportedAt: new Date().toISOString(),
          version: "1.0.0",
        };

        analytics.track("preferences_exported", "settings");
        return JSON.stringify(exportData, null, 2);
      },

      importPreferences: (preferencesJson: string) => {
        try {
          const importData = JSON.parse(preferencesJson);

          // Validate structure
          if (!importData.version || !importData.accessibility) {
            throw new Error("Invalid preferences format");
          }

          // Merge with current preferences (don't overwrite everything)
          const currentState = get();
          set({
            ...currentState,
            accessibility: {
              ...currentState.accessibility,
              ...importData.accessibility,
            },
            audio: { ...currentState.audio, ...importData.audio },
            interface: { ...currentState.interface, ...importData.interface },
            privacy: { ...currentState.privacy, ...importData.privacy },
            features: { ...currentState.features, ...importData.features },
            context: { ...currentState.context, ...importData.context },
          });

          analytics.track("preferences_imported", "settings", {
            version: importData.version,
            importedAt: importData.exportedAt,
          });

          return true;
        } catch (error) {
          analytics.track("preferences_import_failed", "error", {
            error: error instanceof Error ? error.message : "Unknown error",
          });
          return false;
        }
      },

      getPreferencesSummary: () => {
        const state = get();
        return {
          accessibilityEnabled:
            state.accessibility.reducedMotion ||
            state.accessibility.fontSize !== "medium",
          voiceEnabled: state.audio.voiceEnabled,
          ttsEnabled: state.audio.ttsEnabled,
          darkMode: state.interface.theme === "dark",
          language: state.interface.language,
          analyticsEnabled: state.privacy.analyticsEnabled,
          premiumUser: state.features.premiumFeatures,
          onboardingComplete: state.onboarding.completed,
          savedPreferences: Object.keys(state.context).length > 2,
        };
      },
    }),
    {
      name: "rightnowaid-preferences",
      version: 1,
      migrate: (persistedState: unknown, version: number) => {
        // Handle migration from older versions
        if (version < 1) {
          return {
            ...defaultPreferences,
            ...(persistedState as Record<string, unknown>),
          };
        }
        return persistedState as UserPreferencesStore;
      },
    }
  )
);

// Helper hooks for specific preference categories
export const useAccessibilityPreferences = () => {
  const accessibility = useUserPreferencesStore((state) => state.accessibility);
  const updateAccessibility = useUserPreferencesStore(
    (state) => state.updateAccessibility
  );
  return { accessibility, updateAccessibility };
};

export const useAudioPreferences = () => {
  const audio = useUserPreferencesStore((state) => state.audio);
  const updateAudio = useUserPreferencesStore((state) => state.updateAudio);
  return { audio, updateAudio };
};

export const useInterfacePreferences = () => {
  const interface_ = useUserPreferencesStore((state) => state.interface);
  const updateInterface = useUserPreferencesStore(
    (state) => state.updateInterface
  );
  return { interface: interface_, updateInterface };
};

export const usePrivacyPreferences = () => {
  const privacy = useUserPreferencesStore((state) => state.privacy);
  const updatePrivacy = useUserPreferencesStore((state) => state.updatePrivacy);
  return { privacy, updatePrivacy };
};

export const useFeaturePreferences = () => {
  const features = useUserPreferencesStore((state) => state.features);
  const updateFeatures = useUserPreferencesStore(
    (state) => state.updateFeatures
  );
  return { features, updateFeatures };
};

export const useUserContext = () => {
  const context = useUserPreferencesStore((state) => state.context);
  const updateContext = useUserPreferencesStore((state) => state.updateContext);
  return { context, updateContext };
};

export const useOnboardingState = () => {
  const onboarding = useUserPreferencesStore((state) => state.onboarding);
  const updateOnboarding = useUserPreferencesStore(
    (state) => state.updateOnboarding
  );
  return { onboarding, updateOnboarding };
};
